import { Alert, Box, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { GraphLineChart } from '../../analytics-components/graphs/graph-line-chart'
import gaiaTheme from '../../../../root-components/theme'
import { ICrpFigures } from '../../../../shared/services/crp-report/crp-report.model'

/**
 * Step 3 — the Annex A narrative sections. Seeded from the targets and reduction measures already
 * recorded in the Reduction Plan area so a first draft is reviewable rather than blank; once edited
 * the text belongs to the plan and is not refreshed from that source again.
 */
export const CrpStepNarratives: FunctionComponent<{
  values: Record<string, string>
  isProgress: boolean
  figures: ICrpFigures
  onChange: (field: string, value: string) => void
}> = ({ values, isProgress, figures, onChange }) => {
  const graph = figures.projectionGraph || []
  // Actual and projected are separate series in the data; a single "actual" line should continue into
  // the projection rather than stopping dead, so fall back across the three sources per year.
  const dataset = graph.map((point) => ({
    year: String(point.year),
    actual: point.actualValue ?? point.currentValue ?? null,
    target: point.targetValue ?? null,
    projected: point.projectedValue ?? null,
  }))

  const handler = (field: string) => (event: any) => onChange(field, event.target.value)

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Targets and carbon reduction projects</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        We have pre-filled these from the targets and reduction measures recorded in your Reduction Plan.
        Edit them so they read as you want them published.
      </Typography>

      {!isProgress && (
        <Box sx={{ mb: 3 }}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={false}
            title='Commitment to achieving Net Zero'
            name='commitmentText'
            value={values.commitmentText || ''}
            onChange={handler('commitmentText')}
            multiline
            rows={2}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
            helperText='PPN 006 requires a commitment to achieving Net Zero by 2050 for UK operations.'
          />
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <FormTextInputDisplay
          isEditPage={false}
          disabled={false}
          title='Emissions reduction targets'
          name='targetsNarrative'
          value={values.targetsNarrative || ''}
          onChange={handler('targetsNarrative')}
          multiline
          rows={4}
          forceOutlined
          regularFieldLabel
          isTopRow
          isBottomRow
          helperText='Describe your reduction targets and the path to net zero.'
        />
      </Box>

      {dataset.length > 1
        ? (
          <Box sx={{ mb: 4 }}>
            <GraphLineChart
              title='Emissions against target'
              tooltip='Your organisation-wide emissions against target, as recorded in your Reduction Plan. This chart is included in the published plan.'
              dataset={dataset}
              dataKey='year'
              series={[
                { dataKey: 'actual', label: 'Actual tCO2e' },
                { dataKey: 'target', label: 'Target tCO2e' },
                { dataKey: 'projected', label: 'Projected tCO2e' },
              ]}
              colors={[gaiaTheme.palette.primary.main, gaiaTheme.palette.error.main, gaiaTheme.palette.info.main]}
            />
          </Box>
        )
        : (
          <Alert severity='info' sx={{ mb: 4 }}>
            No reduction targets are recorded in your Reduction Plan, so the plan will be published without a
            projection graph. Add targets under Reduction Plan → Targets to include one.
          </Alert>
        )}

      <Box sx={{ mb: 3 }}>
        <FormTextInputDisplay
          isEditPage={false}
          disabled={false}
          title='Completed carbon reduction initiatives'
          name='completedMeasuresNarrative'
          value={values.completedMeasuresNarrative || ''}
          onChange={handler('completedMeasuresNarrative')}
          multiline
          rows={5}
          forceOutlined
          regularFieldLabel
          isTopRow
          isBottomRow
          helperText='Measures completed since the baseline year, and their impact.'
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormTextInputDisplay
          isEditPage={false}
          disabled={false}
          title={isProgress ? 'Measures under way and planned' : 'Planned or in-progress measures'}
          name='plannedMeasuresNarrative'
          value={values.plannedMeasuresNarrative || ''}
          onChange={handler('plannedMeasuresNarrative')}
          multiline
          rows={5}
          forceOutlined
          regularFieldLabel
          isTopRow
          isBottomRow
          helperText='Measures you intend to implement in future.'
        />
      </Box>

      {isProgress
        ? (
          <Box sx={{ mb: 3 }}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Progress commentary'
              name='progressNarrative'
              value={values.progressNarrative || ''}
              onChange={handler('progressNarrative')}
              multiline
              rows={4}
              forceOutlined
              regularFieldLabel
              isTopRow
              isBottomRow
              helperText='Explain your progress against target, including anything that has helped or held it back.'
            />
          </Box>
        )
        : (
          <>
            <Box sx={{ mb: 3 }}>
              <FormTextInputDisplay
                isEditPage={false}
                disabled={false}
                title='Additional details relating to the baseline emissions calculations'
                name='baselineAdditionalDetails'
                value={values.baselineAdditionalDetails || ''}
                onChange={handler('baselineAdditionalDetails')}
                multiline
                rows={3}
                forceOutlined
                regularFieldLabel
                isTopRow
                isBottomRow
                helperText='Optional. For example, a baseline that departs from the standard requirements, or a new baseline set after restructuring.'
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <FormTextInputDisplay
                isEditPage={false}
                disabled={false}
                title='Additional details relating to the current reporting year'
                name='currentAdditionalDetails'
                value={values.currentAdditionalDetails || ''}
                onChange={handler('currentAdditionalDetails')}
                multiline
                rows={3}
                forceOutlined
                regularFieldLabel
                isTopRow
                isBottomRow
                helperText='Optional.'
              />
            </Box>
          </>
        )}
    </>
  )
}
